// Markup from design/kit/components/Sidebar and Badge.
import { useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router';
import type { Api, Me } from '../auth/api';
import { managesDepartment, seesAllRenewals } from '../roles';
import type { NavCounts } from './nav-counts';
import { ProfileMenu } from './ProfileMenu';
import { InboxIcon, ListIcon, SearchIcon, SettingsIcon, TrashIcon } from './icons';

type SidebarProps = {
  me: Me;
  counts: NavCounts;
  api: Api;
};

const navClass = ({ isActive }: { isActive: boolean }) => (isActive ? 'nav active' : 'nav');

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

type NavItemProps = {
  to: string;
  icon: ReactNode;
  label: string;
  count?: number;
  // urgent: red count with a dot (needs attention); otherwise a muted count
  urgent?: boolean;
  countLabel?: string;
};

function NavItem({ to, icon, label, count = 0, urgent = false, countLabel }: NavItemProps) {
  return (
    <NavLink className={navClass} to={to}>
      {icon}
      <span className="grow ell">{label}</span>
      {count > 0 && (
        <span className={urgent ? 'cnt urg' : 'cnt'} aria-label={countLabel}>
          {count}
        </span>
      )}
    </NavLink>
  );
}

const LISTS = ['/renewals/my', '/renewals/all'];

/**
 * Global search (spec C7.2): insured or policy number, across every tab. On a list it filters as you
 * type; anywhere else Enter opens the list with the search applied. The text lives in the list's
 * address (?q=), so Back from the Record keeps it.
 */
function SidebarSearch({ home }: { home: string }) {
  const location = useLocation();
  const navigate = useNavigate();
  const onList = LISTS.includes(location.pathname);
  const inAddress = onList ? (new URLSearchParams(location.search).get('q') ?? '') : '';
  const [text, setText] = useState(inAddress);
  // The address changed by itself (Clear search, another page): show what it says.
  const [seen, setSeen] = useState(inAddress);
  if (inAddress !== seen) {
    setSeen(inAddress);
    setText(inAddress);
  }

  const go = (value: string, pathname: string) => {
    const params = new URLSearchParams(onList ? location.search : '');
    // A search looks across every tab and stat card.
    params.delete('stat');
    if (value.trim()) params.set('q', value);
    else params.delete('q');
    const search = params.toString();
    navigate(
      { pathname, search: search ? `?${search}` : '' },
      { replace: onList, state: onList ? location.state : undefined },
    );
  };

  return (
    <div className="searchbox">
      <SearchIcon />
      <label htmlFor="sb-q" className="sr">
        Search insureds and policy numbers
      </label>
      <input
        id="sb-q"
        className="input"
        type="search"
        placeholder="Search insureds, policies"
        autoComplete="off"
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          if (onList) go(e.target.value, location.pathname);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !onList && text.trim()) go(text, home);
        }}
      />
    </div>
  );
}

export function Sidebar({ me, counts, api }: SidebarProps) {
  const { user } = me;
  const manager = managesDepartment(user.role);
  return (
    <aside className="side">
      <Link className="brand" to="/renewals/my">
        <span className="brand-in">
          <span className="ss-logo ss-logo-side" role="img" aria-label="Safeside Insurance"></span>
        </span>
      </Link>
      <SidebarSearch home={seesAllRenewals(user.role) ? '/renewals/all' : '/renewals/my'} />
      <nav aria-label="Main" className="snavs">
        <div className="nsec" role="group" aria-label="Renewals">
          <div className="nsec-l">Renewals</div>
          <NavItem
            to="/renewals/my"
            icon={<InboxIcon />}
            label="My renewals"
            count={counts.myRenewals}
            urgent
            countLabel={`${counts.myRenewals} need attention`}
          />
          {seesAllRenewals(user.role) && (
            <NavItem
              to="/renewals/all"
              icon={<ListIcon />}
              label="All renewals"
              count={counts.allRenewals}
              urgent
              countLabel={`${counts.allRenewals} need attention`}
            />
          )}
          {manager && (
            <NavItem
              to="/deleted"
              icon={<TrashIcon />}
              label="Deleted records"
              count={counts.deleteRequests}
              countLabel={plural(counts.deleteRequests, 'delete request', 'delete requests')}
            />
          )}
        </div>
      </nav>
      <div className="grow"></div>
      <div className="sbot">
        <div className="sdiv" role="separator"></div>
        {manager && <NavItem to="/settings" icon={<SettingsIcon />} label="Settings" />}
        <ProfileMenu me={me} api={api} />
      </div>
    </aside>
  );
}
